import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useTranslations } from 'hooks/useTranslations';
import { useParams } from 'react-router-dom';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Grid, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormInput } from 'components/molecules/FormInput';
import { FormWyswigEditor } from 'components/molecules/FormWyswigEditor';
import { FormModal } from 'components/molecules/Modal';
import { Loader } from 'components/atoms/Loader';
import { Alert } from 'components/atoms/Alert';
import { countEditorCharacters } from 'components/organisms/HtmlSlate';
import { ICommentFormValues } from 'models/cases';
import { useCase } from 'pages/CaseManagementPage/CasesPage/hooks';
import { Elabels } from 'pages/CaseManagementPage/CasesPage/helpers';
import { CaseThreadsAccordions } from 'pages/CaseManagementPage/CasesPage/CaseDetailsPage/Comments/CaseThreadsAccordions';

const initialFormValues: ICommentFormValues = { comment: '', topic: '' };

interface IComments {
  isReadOnly?: boolean;
  isAssignUser: boolean;
  isSupervisor: boolean;
}

const Comments = ({ isReadOnly = false, isAssignUser, isSupervisor }: IComments) => {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const { t } = useTranslations();
  const { modalAlert, clearAlert } = useAlert();
  const { getCaseThreadsData, caseThreadsData, addCaseThread, addCaseThreadComment } =
    useCase();
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const schema = yup.object({
    topic: yup
      .string()
      .required(t('general.field.validation.required'))
      .max(255, t('general.field.validation.maxCharacters').replace('{0}', '255'))
      .trim(),
    comment: yup
      .string()
      .required(t('general.field.validation.required'))
      .test(
        'editor-validation',
        t('general.field.validation.maxCharacters').replace('{0}', '1000'),
        (value) => countEditorCharacters(value) <= 1000,
      )
      .trim(),
  });

  useEffect(() => {
    (async function init() {
      setIsLoading(true);
      await getCaseThreadsData(caseId);
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
  }, [isOpenModal]);

  const reloadCaseThreads = async () => {
    await getCaseThreadsData(caseId);
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<ICommentFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<ICommentFormValues>,
  });

  const handleClose = () => {
    setIsOpenModal(false);
    reset(initialFormValues);
  };

  const submitForm = async (data: ICommentFormValues) => {
    const res = await addCaseThread(caseId, data);
    if (res) {
      handleClose();
      reloadCaseThreads();
    }
  };

  return (
    <>
      {isLoading && <Loader isLoading />}
      {!isLoading && (
        <Stack sx={{ gap: '20px' }}>
          <Grid
            container
            sx={{ justifyContent: 'space-between', alignItems: 'center', rowGap: '10px' }}
          >
            <Grid size={{ xs: 12, md: 'auto' }}>
              <Typography variant="subtitle2">
                {`${t(Elabels.totalThreads)}: ${caseThreadsData.length}`}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 'auto' }}>
              {(isAssignUser || isSupervisor) && !isReadOnly && (
                <Button
                  variant={EButtonVariants.contained}
                  label={t(Elabels.addComment)}
                  size={EButtonSizes.medium}
                  icon={EIconTypes.plus}
                  onClick={() => setIsOpenModal(true)}
                  id="comments_add-button"
                  fullWidth
                />
              )}
            </Grid>
          </Grid>

          <CaseThreadsAccordions
            items={caseThreadsData}
            maxVisibleItems={6}
            onReload={reloadCaseThreads}
            onSubmitReply={(threadId, body) => addCaseThreadComment(caseId, threadId, body)}
            isReadOnly={isReadOnly}
            isAssignUser={isAssignUser}
            isSupervisor={isSupervisor}
          />

          <FormModal
            open={isOpenModal}
            onClickClose={handleClose}
            onClickApprove={handleSubmit(submitForm)}
            title={t(Elabels.addComment)}
            approveButtonLabel="general.ok.label"
            cancelButtonLabel="general.cancel.button"
            maxWidth="1200px"
            width="85%"
            titleLeft
          >
            {modalAlert && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Alert text={modalAlert.text} variant={modalAlert.variant} />
              </Box>
            )}
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <FormInput
                  name="topic"
                  label={t(Elabels.topic)}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                  charsLimit={255}
                  required
                  id="comments_topic-input"
                />
              </Grid>
              <Grid size={{ xs: 12 }} sx={{ minHeight: '50vh' }}>
                <FormWyswigEditor
                  name="comment"
                  label={t(Elabels.comment)}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                  setError={setError}
                  clearErrors={clearErrors}
                  charsLimit={1000}
                  showCounter
                  required
                  fullContainer
                />
              </Grid>
            </Grid>
          </FormModal>
        </Stack>
      )}
    </>
  );
};

export default Comments;
